import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, Phone, Plug, Target, Check, ChartLineUp, GoogleLogo, FacebookLogo } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Connect Email", "Connect Phone", "Connect Ad Accounts", "Set Campaign Goals", "All Set"];

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [maxCpl, setMaxCpl] = useState("30");
  const [minRoas, setMinRoas] = useState("3");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      setStep(2);
      refreshAccounts();
      window.history.replaceState(null, "", "/onboarding");
    }
  }, []);

  const refreshAccounts = async () => {
    const res = await api.get("/integrations/accounts");
    setConnectedAccounts(res.data);
  };

  const handleConnectEmail = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/email", { contact_email: contactEmail });
      toast.success("Email connected");
      setStep(1);
    } catch (e) {
      toast.error("Failed to connect email");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPhone = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/phone", { phone_number: phone, sms_consent: smsConsent });
      toast.success("Phone connected");
      setStep(2);
    } catch (e) {
      toast.error("Failed to connect phone");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAdAccount = async (platform) => {
    setLoading(true);
    try {
      const res = await api.get(`/integrations/${platform}-ads/connect`);
      if (res.data.direct) {
        await api.post(`/integrations/${platform}-ads/direct-connect`);
        toast.success(`${platform === "google" ? "Google" : "Meta"} Ads connected with your live account data`);
        await refreshAccounts();
      } else if (res.data.mock) {
        await api.post(`/integrations/${platform}-ads/mock-connect`);
        toast.success(`${platform === "google" ? "Google" : "Meta"} Ads connected (demo data)`);
        await refreshAccounts();
      } else {
        window.top.location.href = res.data.auth_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to connect account");
    } finally {
      setLoading(false);
    }
  };

  const handleSkipToDemoData = async (platform) => {
    setLoading(true);
    try {
      await api.post(`/integrations/${platform}-ads/mock-connect`);
      toast.success(`${platform === "google" ? "Google" : "Meta"} Ads connected (demo data)`);
      await refreshAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set up demo data");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoals = async () => {
    setLoading(true);
    try {
      const campaignsRes = await api.get("/campaigns");
      const goals = {
        max_cpl: maxCpl ? parseFloat(maxCpl) : null,
        pause_on_cpl_exceed: true,
        min_roas_for_scale: minRoas ? parseFloat(minRoas) : null,
        increase_spend_on_high_roas: true,
        scale_percent: 20,
      };
      await Promise.all(campaignsRes.data.map((c) => api.patch(`/campaigns/${c.id}`, { goals })));
      setStep(4);
    } catch (e) {
      toast.error("Failed to save campaign goals");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete");
      await refreshUser();
      navigate("/dashboard");
    } catch (e) {
      toast.error("Failed to complete onboarding");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Dialog open={true}>
        <DialogContent hideClose className="max-w-lg" data-testid="onboarding-modal">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-md bg-primary/15 flex items-center justify-center">
              <ChartLineUp className="h-4.5 w-4.5 text-primary" weight="bold" />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">AdCrew OS Setup</span>
          </div>
          <Progress value={(step / (STEPS.length - 1)) * 100} className="mb-1" data-testid="onboarding-progress" />
          <p className="text-xs text-muted-foreground mb-4">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}: {STEPS[step]}</p>

          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Envelope className="h-6 w-6 text-primary" weight="bold" />
              </div>
              <h2 className="font-heading font-semibold text-xl">Connect your email</h2>
              <p className="text-sm text-muted-foreground">We'll send weekly performance summaries and critical alerts here.</p>
              <div className="space-y-1.5">
                <Label htmlFor="contact-email">Contact email</Label>
                <Input id="contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="onboarding-email-input" />
              </div>
              <Button className="w-full" onClick={handleConnectEmail} disabled={loading || !contactEmail} data-testid="onboarding-connect-email-button">
                {loading ? "Connecting..." : "Connect Email"}
              </Button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Phone className="h-6 w-6 text-primary" weight="bold" />
              </div>
              <h2 className="font-heading font-semibold text-xl">Connect your phone</h2>
              <p className="text-sm text-muted-foreground">Get instant SMS alerts when a campaign crosses a threshold.</p>
              <div className="space-y-1.5">
                <Label htmlFor="phone-number">Phone number (E.164 format)</Label>
                <Input id="phone-number" type="tel" placeholder="+14155551234" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="onboarding-phone-input" />
              </div>
              <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer" data-testid="onboarding-sms-consent-label">
                <Checkbox checked={smsConsent} onCheckedChange={setSmsConsent} className="mt-0.5" data-testid="onboarding-sms-consent-checkbox" />
                <span>
                  By entering your phone number and checking this box, you agree to receive text messages from AdCrewOS
                  about account activity, onboarding, and service updates. Message and data rates may apply. Reply STOP
                  to opt out, HELP for help.
                </span>
              </label>
              <Button className="w-full" onClick={handleConnectPhone} disabled={loading || !phone || !smsConsent} data-testid="onboarding-connect-phone-button">
                {loading ? "Connecting..." : "Connect Phone"}
              </Button>
              <button className="text-xs text-muted-foreground hover:text-foreground w-full text-center" onClick={() => setStep(2)} data-testid="onboarding-skip-phone-button">Skip for now</button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Plug className="h-6 w-6 text-primary" weight="bold" />
              </div>
              <h2 className="font-heading font-semibold text-xl">Connect your ad accounts</h2>
              <p className="text-sm text-muted-foreground">Link Google Ads and/or Meta Ads to start monitoring campaigns.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Button variant="outline" className="h-16 flex-col gap-1" onClick={() => handleConnectAdAccount("google")} disabled={loading} data-testid="onboarding-connect-google-button">
                    <GoogleLogo className="h-5 w-5" />
                    <span className="text-xs">Google Ads</span>
                  </Button>
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => handleSkipToDemoData("google")} disabled={loading} data-testid="onboarding-skip-google-demo-button">
                    Skip to use demo data
                  </button>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button variant="outline" className="h-16 flex-col gap-1" onClick={() => handleConnectAdAccount("meta")} disabled={loading} data-testid="onboarding-connect-meta-button">
                    <FacebookLogo className="h-5 w-5" />
                    <span className="text-xs">Meta Ads</span>
                  </Button>
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => handleSkipToDemoData("meta")} disabled={loading} data-testid="onboarding-skip-meta-demo-button">
                    Skip to use demo data
                  </button>
                </div>
              </div>
              {connectedAccounts.length > 0 && (
                <div className="rounded-md border border-border p-3 space-y-1.5">
                  {connectedAccounts.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`onboarding-connected-account-${a.id}`}>
                      <Check className="h-4 w-4 text-success" weight="bold" />
                      <span>{a.account_name}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button className="w-full" onClick={() => setStep(3)} disabled={connectedAccounts.length === 0} data-testid="onboarding-continue-to-goals-button">
                Continue
              </Button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Target className="h-6 w-6 text-primary" weight="bold" />
              </div>
              <h2 className="font-heading font-semibold text-xl">Set your campaign goals</h2>
              <p className="text-sm text-muted-foreground">AdCrew OS will automatically act on these rules for all connected campaigns.</p>
              <div className="space-y-1.5">
                <Label htmlFor="onb-max-cpl">Pause campaigns when CPL exceeds ($)</Label>
                <Input id="onb-max-cpl" type="number" value={maxCpl} onChange={(e) => setMaxCpl(e.target.value)} data-testid="onboarding-max-cpl-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="onb-min-roas">Increase spend when ROAS exceeds (x)</Label>
                <Input id="onb-min-roas" type="number" value={minRoas} onChange={(e) => setMinRoas(e.target.value)} data-testid="onboarding-min-roas-input" />
              </div>
              <Button className="w-full" onClick={handleSaveGoals} disabled={loading} data-testid="onboarding-save-goals-button">
                {loading ? "Saving..." : "Save Goals & Continue"}
              </Button>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 text-center">
              <div className="h-14 w-14 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                <Check className="h-7 w-7 text-success" weight="bold" />
              </div>
              <h2 className="font-heading font-semibold text-xl">You're all set!</h2>
              <p className="text-sm text-muted-foreground">Your AdCrew OS cockpit is ready. Let's see your campaigns.</p>
              <Button className="w-full" onClick={handleFinish} disabled={loading} data-testid="onboarding-go-to-dashboard-button">
                {loading ? "Finishing..." : "Go to Dashboard"}
              </Button>
            </motion.div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
